// Library Methods
import { useCallback, useMemo } from "react";

// Components
import Skeleton from "@mui/material/Skeleton";
import Square from "src/assets/shapes/Square/Square";
import Dash from "src/assets/shapes/Dash/Dash";
import ColoredCircle from "src/assets/shapes/ColoredCircle/ColoredCircle";
import Circle from "src/assets/shapes/Circle/Circle";
import ChartCross from "src/assets/shapes/ChartCross/ChartCross";
import GraphRings from "src/assets/shapes/GraphRings/GraphRings";

// Types
import { ChartSpotType } from "src/services/exams/types";

const DEFAULT_CONTAINER_SIZE = 624;

type Full120ChartType = {
  full120Spots: Array<ChartSpotType>;
  containerSize?: number;
  isLiveUpdate?: boolean;
};

const Full120Chart = ({
  full120Spots,
  containerSize = DEFAULT_CONTAINER_SIZE,
  isLiveUpdate = false,
}: Full120ChartType) => {
  // Add these values as params if they can change in the future
  const gridType = useMemo(() => "FULL_120", []);
  const tickNum = useMemo(() => 6, []);

  const size = useMemo(() => containerSize * 0.7, [containerSize]);
  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / DEFAULT_CONTAINER_SIZE, [size]);

  const calculateXPaddingFull120Chart = useCallback(
    (xFloor: number) => {
      // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
      const jumpAxis = 306 * scaleFactor;
      const paddingPerUnit = 5.2 * scaleFactor;

      if (xFloor <= 0) {
        return 310 * scaleFactor - xFloor * -paddingPerUnit;
      } else {
        return xFloor * paddingPerUnit + jumpAxis;
      }
    },
    [scaleFactor]
  );

  const calculateYPaddingFull120Chart = useCallback(
    (yFloor: number) => {
      // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
      const jumpAxis = 306 * scaleFactor;

      if (yFloor >= 0) {
        return 308 * scaleFactor - yFloor * 5.2 * scaleFactor;
      } else {
        return jumpAxis + yFloor * -5.3 * scaleFactor;
      }
    },
    [scaleFactor]
  );

  // Show loading skeleton if data is not yet processed
  if (full120Spots?.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <GraphRings size={size} tickNum={tickNum} ariaLabel="full120-ring" />

      <ChartCross
        height={size}
        width={size}
        tickNum={tickNum}
        tickSize={tickSize}
        gridType={gridType}
      />

      {/* Unseen spots */}
      {full120Spots
        ?.filter((spot) => !spot?.seen && spot?.completed)
        ?.map((spot, i) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingFull120Chart(spot?.x),
                paddingTop: calculateYPaddingFull120Chart(spot?.y),
              }}
            >
              <Square size={scaleFactor * 8} />
            </div>
          );
        })}

      {/* Seen spots */}
      {full120Spots
        ?.filter((spot) => spot?.seen)
        ?.map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingFull120Chart(spot?.x),
                paddingTop: calculateYPaddingFull120Chart(spot?.y),
              }}
            >
              <Circle size={scaleFactor * 10} />
            </div>
          );
        })}

      {/* In progress spots */}
      {full120Spots
        ?.filter((spot) => !spot?.completed && spot?.decibelValue) // filter uncompleted spots with a valid decibelValue
        ?.map((spot, i: number) => {
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                paddingLeft: calculateXPaddingFull120Chart(spot?.x),
                paddingTop: calculateYPaddingFull120Chart(spot?.y),
              }}
            >
              {!isLiveUpdate ? (
                <Dash size={8} />
              ) : (
                <ColoredCircle
                  isOrange={!spot?.current}
                  size={scaleFactor * 8}
                />
              )}
            </div>
          );
        })}
    </main>
  );
};

export default Full120Chart;
